import { IAppSubscriptionPublicInfo } from '../types/App.model';

/**
 * Checks if a user has valid access to Web Catalogue apps
 * A valid access requires:
 * 1. At least one subscription with expiration date in the future
 * 2. Subscription must be linked to a license
 * 3. License must be linked to an app
 * 4. App must have "web" in its platforms array
 * 
 * Note: webCatalogue feature flag is NOT checked here as it's only a subfunction
 * for loading web models, not for determining app accessibility
 * 
 * @param subscriptions - Array of user's app subscription info (subscription + license + app)
 * @returns true if user has at least one valid subscription for a web platform app, false otherwise
 */
export function hasValidWebCatalogueLicense(
  subscriptions: IAppSubscriptionPublicInfo[]
): boolean {
  if (!subscriptions || subscriptions.length === 0) {
    return false;
  }

  const now = new Date();

  // Check if any subscription provides access to a web platform app
  return subscriptions.some((subscriptionInfo) => {
    // 1. Check if subscription is not expired
    const expiryDate = new Date(subscriptionInfo.subscription.expirationDate);
    const isActive = expiryDate > now;

    if (!isActive) {
      return false;
    }

    // 2. Check if license exists (subscription → license)
    if (!subscriptionInfo.license || !subscriptionInfo.license.app_id) {
      return false;
    }

    // 3. Check if app exists (license → app)
    if (!subscriptionInfo.app || !subscriptionInfo.app.plattforms) {
      return false;
    }

    // 4. Check if app has "web" in its platforms array
    const platforms = subscriptionInfo.app.plattforms;
    const hasWebPlatform = Array.isArray(platforms) && platforms.includes('web');

    return hasWebPlatform;
  });
}

/**
 * Checks if a user has valid access to Web Catalogue apps (alias for clarity)
 * This is the same as hasValidWebCatalogueLicense but with a more descriptive name
 * 
 * @param subscriptions - Array of user's app subscription info
 * @returns true if user has valid web app access, false otherwise
 */
export function hasValidWebAppAccess(
  subscriptions: IAppSubscriptionPublicInfo[]
): boolean {
  return hasValidWebCatalogueLicense(subscriptions);
}

